/**
 * Box state management via React Context.
 * Wraps the API client, provides box/item data + operations to the tree.
 */

import { createContext, useContext, useReducer, useCallback, useMemo } from 'react';
import type { ReactNode } from 'react';
import type { Box, BoxWithItems, Item } from '@shared/types';
import * as api from '../api/client';
import type { ListParams } from '../api/client';
import { trackEvent } from '@shared/plausible';

// ── State ─────────────────────────────────────────────────────

interface BoxState {
  boxes: Box[];
  total: number;
  page: number;
  hasMore: boolean;
  loading: boolean;
  error: string | null;

  currentBox: BoxWithItems | null;
  currentBoxLoading: boolean;
  currentBoxError: string | null;

  searchResults: BoxWithItems[];
  searchLoading: boolean;
  searchQuery: string;
}

const initialState: BoxState = {
  boxes: [],
  total: 0,
  page: 1,
  hasMore: false,
  loading: false,
  error: null,

  currentBox: null,
  currentBoxLoading: false,
  currentBoxError: null,

  searchResults: [],
  searchLoading: false,
  searchQuery: '',
};

// ── Actions ───────────────────────────────────────────────────

type BoxAction =
  | { type: 'FETCH_BOXES_START' }
  | { type: 'FETCH_BOXES_SUCCESS'; boxes: Box[]; total: number; page: number; hasMore: boolean }
  | { type: 'FETCH_BOXES_ERROR'; error: string }
  | { type: 'FETCH_BOX_START' }
  | { type: 'FETCH_BOX_SUCCESS'; box: BoxWithItems }
  | { type: 'FETCH_BOX_ERROR'; error: string }
  | { type: 'CLEAR_CURRENT_BOX' }
  | { type: 'BOX_CREATED'; box: Box }
  | { type: 'BOX_UPDATED'; box: Box }
  | { type: 'BOX_DELETED'; id: string }
  | { type: 'ITEM_ADDED'; item: Item }
  | { type: 'ITEM_UPDATED'; item: Item }
  | { type: 'ITEM_DELETED'; id: string }
  | { type: 'SEARCH_START'; query: string }
  | { type: 'SEARCH_SUCCESS'; results: BoxWithItems[] }
  | { type: 'SEARCH_ERROR'; error: string }
  | { type: 'SEARCH_CLEAR' };

function boxReducer(state: BoxState, action: BoxAction): BoxState {
  switch (action.type) {
    case 'FETCH_BOXES_START':
      return { ...state, loading: true, error: null };

    case 'FETCH_BOXES_SUCCESS':
      return {
        ...state,
        loading: false,
        boxes: action.page === 1 ? action.boxes : [...state.boxes, ...action.boxes],
        total: action.total,
        page: action.page,
        hasMore: action.hasMore,
      };

    case 'FETCH_BOXES_ERROR':
      return { ...state, loading: false, error: action.error };

    case 'FETCH_BOX_START':
      return { ...state, currentBoxLoading: true, currentBoxError: null };

    case 'FETCH_BOX_SUCCESS':
      return { ...state, currentBoxLoading: false, currentBox: action.box };

    case 'FETCH_BOX_ERROR':
      return { ...state, currentBoxLoading: false, currentBoxError: action.error };

    case 'CLEAR_CURRENT_BOX':
      return { ...state, currentBox: null, currentBoxError: null };

    case 'BOX_CREATED':
      return {
        ...state,
        boxes: [action.box, ...state.boxes],
        total: state.total + 1,
      };

    case 'BOX_UPDATED':
      return {
        ...state,
        boxes: state.boxes.map((b) => (b.id === action.box.id ? action.box : b)),
        currentBox:
          state.currentBox?.id === action.box.id
            ? { ...state.currentBox, ...action.box }
            : state.currentBox,
      };

    case 'BOX_DELETED':
      return {
        ...state,
        boxes: state.boxes.filter((b) => b.id !== action.id),
        total: state.total - 1,
        currentBox: state.currentBox?.id === action.id ? null : state.currentBox,
      };

    case 'ITEM_ADDED':
      if (!state.currentBox) return state;
      return {
        ...state,
        currentBox: {
          ...state.currentBox,
          items: [...state.currentBox.items, action.item],
        },
      };

    case 'ITEM_UPDATED':
      if (!state.currentBox) return state;
      return {
        ...state,
        currentBox: {
          ...state.currentBox,
          items: state.currentBox.items.map((i) => (i.id === action.item.id ? action.item : i)),
        },
      };

    case 'ITEM_DELETED':
      if (!state.currentBox) return state;
      return {
        ...state,
        currentBox: {
          ...state.currentBox,
          items: state.currentBox.items.filter((i) => i.id !== action.id),
        },
      };

    case 'SEARCH_START':
      return { ...state, searchLoading: true, searchQuery: action.query };

    case 'SEARCH_SUCCESS':
      return { ...state, searchLoading: false, searchResults: action.results };

    case 'SEARCH_ERROR':
      return { ...state, searchLoading: false };

    case 'SEARCH_CLEAR':
      return { ...state, searchResults: [], searchQuery: '', searchLoading: false };

    default:
      return state;
  }
}

// ── Context ───────────────────────────────────────────────────

interface BoxContextValue {
  state: BoxState;
  fetchBoxes: (params?: ListParams) => Promise<void>;
  fetchBox: (id: string) => Promise<void>;
  clearCurrentBox: () => void;
  createBox: (data: api.CreateBoxBody) => Promise<Box>;
  updateBox: (id: string, data: api.UpdateBoxBody) => Promise<Box>;
  removeBox: (id: string) => Promise<void>;
  addItem: (boxId: string, data: api.CreateItemBody) => Promise<Item>;
  updateItem: (id: string, data: api.UpdateItemBody) => Promise<Item>;
  adjustItemQuantity: (id: string, delta: number) => Promise<Item>;
  removeItem: (id: string) => Promise<void>;
  search: (q: string) => Promise<void>;
  clearSearch: () => void;
}

const BoxContext = createContext<BoxContextValue | null>(null);

// ── Provider ──────────────────────────────────────────────────

interface BoxProviderProps {
  children: ReactNode;
}

export function BoxProvider({ children }: BoxProviderProps) {
  const [state, dispatch] = useReducer(boxReducer, initialState);

  const fetchBoxes = useCallback(async (params?: ListParams) => {
    dispatch({ type: 'FETCH_BOXES_START' });
    try {
      const result = await api.listBoxes(params);
      dispatch({
        type: 'FETCH_BOXES_SUCCESS',
        boxes: result.data,
        total: result.total,
        page: result.page,
        hasMore: result.hasMore,
      });
    } catch (err) {
      dispatch({
        type: 'FETCH_BOXES_ERROR',
        error: err instanceof Error ? err.message : 'Failed to load boxes',
      });
    }
  }, []);

  const fetchBox = useCallback(async (id: string) => {
    dispatch({ type: 'FETCH_BOX_START' });
    try {
      const box = await api.getBox(id);
      dispatch({ type: 'FETCH_BOX_SUCCESS', box });
    } catch (err) {
      dispatch({
        type: 'FETCH_BOX_ERROR',
        error: err instanceof Error ? err.message : 'Failed to load box',
      });
    }
  }, []);

  const clearCurrentBox = useCallback(() => {
    dispatch({ type: 'CLEAR_CURRENT_BOX' });
  }, []);

  const createBoxFn = useCallback(async (data: api.CreateBoxBody): Promise<Box> => {
    const box = await api.createBox(data);
    dispatch({ type: 'BOX_CREATED', box });
    trackEvent('Box Created');
    return box;
  }, []);

  const updateBoxFn = useCallback(async (id: string, data: api.UpdateBoxBody): Promise<Box> => {
    const box = await api.updateBox(id, data);
    dispatch({ type: 'BOX_UPDATED', box });
    return box;
  }, []);

  const removeBox = useCallback(async (id: string) => {
    await api.deleteBox(id);
    dispatch({ type: 'BOX_DELETED', id });
  }, []);

  const addItemFn = useCallback(async (boxId: string, data: api.CreateItemBody): Promise<Item> => {
    const item = await api.addItem(boxId, data);
    dispatch({ type: 'ITEM_ADDED', item });
    trackEvent('Item Added');
    return item;
  }, []);

  const updateItemFn = useCallback(async (id: string, data: api.UpdateItemBody): Promise<Item> => {
    const item = await api.updateItem(id, data);
    dispatch({ type: 'ITEM_UPDATED', item });
    return item;
  }, []);

  const adjustItemQuantityFn = useCallback(async (id: string, delta: number): Promise<Item> => {
    const item = await api.adjustItemQuantity(id, delta);
    dispatch({ type: 'ITEM_UPDATED', item });
    return item;
  }, []);

  const removeItem = useCallback(async (id: string) => {
    await api.deleteItem(id);
    dispatch({ type: 'ITEM_DELETED', id });
  }, []);

  const searchFn = useCallback(async (q: string) => {
    if (!q.trim()) {
      dispatch({ type: 'SEARCH_CLEAR' });
      return;
    }
    dispatch({ type: 'SEARCH_START', query: q });
    try {
      const result = await api.searchBoxes(q);
      dispatch({ type: 'SEARCH_SUCCESS', results: result.data });
    } catch (err) {
      dispatch({
        type: 'SEARCH_ERROR',
        error: err instanceof Error ? err.message : 'Search failed',
      });
    }
  }, []);

  const clearSearch = useCallback(() => {
    dispatch({ type: 'SEARCH_CLEAR' });
  }, []);

  const value = useMemo<BoxContextValue>(
    () => ({
      state,
      fetchBoxes,
      fetchBox,
      clearCurrentBox,
      createBox: createBoxFn,
      updateBox: updateBoxFn,
      removeBox,
      addItem: addItemFn,
      updateItem: updateItemFn,
      adjustItemQuantity: adjustItemQuantityFn,
      removeItem,
      search: searchFn,
      clearSearch,
    }),
    [
      state,
      fetchBoxes,
      fetchBox,
      clearCurrentBox,
      createBoxFn,
      updateBoxFn,
      removeBox,
      addItemFn,
      updateItemFn,
      adjustItemQuantityFn,
      removeItem,
      searchFn,
      clearSearch,
    ],
  );

  return <BoxContext.Provider value={value}>{children}</BoxContext.Provider>;
}

// ── Hook ──────────────────────────────────────────────────────

export function useBoxStore(): BoxContextValue {
  const ctx = useContext(BoxContext);
  if (!ctx) {
    throw new Error('useBoxStore must be used within a BoxProvider');
  }
  return ctx;
}
